import React from 'react';
import VariantDisplay from '../VariantDisplay';
import { ExternalLink } from 'lucide-react';

const ListingRow = ({ listing, index, summary, getPriceColor, getPriceBadge, mostRecentSessionTime }) => {
  const isEbay = listing.marketplace === 'ebay' || (listing.seller_name && listing.seller_name.toLowerCase().includes('ebay'));

  const marketplaceBadge = isEbay ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">eBay</span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Carousell</span>
  );

  const getStatusBadge = () => {
    const scrapedAt = listing.scraped_at ? new Date(listing.scraped_at) : null;
    const isRecent = scrapedAt && (new Date() - scrapedAt) < 24 * 60 * 60 * 1000;
    if (listing.status === 'new' && isRecent) return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-green-500 text-white animate-pulse">NEW!</span>;
    if (listing.status === 'price_changed') {
      const priceChange = listing.price - (listing.previous_price || 0);
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${priceChange > 0 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>{priceChange > 0 ? '📈' : '📉'} Price Changed</span>;
    }
    if (listing.status === 'removed') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-400 text-white line-through">Removed</span>;
    if (listing.status === 'sold') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Sold</span>;
    return null;
  };

  const statusBadge = getStatusBadge();
  const isRemoved = listing.status === 'removed' || listing.status === 'sold';

  return (
    <a href={listing.url} target="_blank" rel="noopener noreferrer"
      className={`block p-3 border border-gray-200 rounded-lg transition-all group ${isRemoved ? 'opacity-50 bg-gray-50' : 'hover:border-red-300 hover:bg-red-50'}`}>
      <div className="flex items-start space-x-3">
        {listing.image_url && (
          <img src={listing.image_url} alt={listing.title} className={`w-16 h-16 object-cover rounded flex-shrink-0 ${isRemoved ? 'grayscale' : ''}`} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
            <h4 className={`font-medium group-hover:text-red-600 line-clamp-2 flex-1 ${isRemoved ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{listing.title}</h4>
            <div className="flex gap-1 flex-wrap">
              {statusBadge}
              {marketplaceBadge}
              {listing.interested_buyers > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                  💰 {listing.interested_buyers} buyer{listing.interested_buyers > 1 ? 's' : ''} interested
                </span>
              )}
            </div>
          </div>

          {listing.has_variants && listing.variants && listing.variants.length > 0 ? (
            <VariantDisplay listing={listing} />
          ) : (
            <div className="flex items-baseline gap-2">
              <p className={`font-bold text-lg ${isRemoved ? 'text-gray-400' : getPriceColor(listing.price)}`}>
                {listing.has_variants && listing.min_price && listing.max_price && listing.min_price !== listing.max_price
                  ? <>S${listing.min_price} - S${listing.max_price}</>
                  : <>S${listing.price}</>
                }
                {!isRemoved && getPriceBadge(listing.price)}
              </p>
              {listing.status === 'price_changed' && listing.previous_price && (
                <p className="text-sm text-gray-500 line-through">S${listing.previous_price}</p>
              )}
              {listing.extraction_method && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {listing.extraction_method === 'llm' ? `LLM ${listing.llm_extraction_time?.toFixed(1)}s` : 'Regex'}
                </span>
              )}
            </div>
          )}

          {listing.has_variants && listing.variant_count > 0 && (
            <p className="text-xs text-gray-500 mt-1">{listing.variant_count} storage option{listing.variant_count > 1 ? 's' : ''} available</p>
          )}
          {listing.seller_name && (
            <p className="text-xs text-gray-500 mt-1">
              by {listing.seller_name}
              {listing.username && listing.username !== listing.seller_name && <span className="text-gray-400"> (@{listing.username})</span>}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            {listing.status === 'new' ? 'First seen: ' : 'Last seen: '}
            {new Date(listing.last_seen || listing.scraped_at).toLocaleDateString()}
          </p>
        </div>
        <ExternalLink className={`w-4 h-4 flex-shrink-0 mt-1 ${isRemoved ? 'text-gray-300' : 'text-gray-400 group-hover:text-red-500'}`} />
      </div>
    </a>
  );
};

export default ListingRow;
